import React from 'react';
import { X, Share, Plus, Smartphone } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

const InstallPrompt = () => {
  const { showPrompt, dismissPrompt } = useInstallPrompt();

  if (!showPrompt) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-fade-in"
        onClick={() => dismissPrompt(false)}
      />

      {/* Install Prompt Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
        <div className="mx-auto max-w-md">
          <div className="relative bg-gradient-to-br from-emerald-800 via-green-900 to-emerald-800 rounded-2xl shadow-2xl border border-emerald-500/30 overflow-hidden">
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/10 via-transparent to-amber-500/10 pointer-events-none" />

            {/* Close button */}
            <button
              onClick={() => dismissPrompt(false)}
              className="absolute top-3 right-3 flex min-h-11 min-w-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm z-10"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-white" />
            </button>

            <div className="relative p-6 pb-5">
              {/* Icon and Title */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Smartphone className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Install Ummah Cards
                  </h3>
                  <p className="text-sm text-emerald-200">
                    Access instantly from your home screen
                  </p>
                </div>
              </div>

              {/* Installation Steps */}
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-3 text-sm">
                  <div className="flex-shrink-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                    1
                  </div>
                  <div className="flex items-center gap-2 text-white pt-0.5">
                    <span>Tap the Share button</span>
                    <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm animate-bounce">
                      <Share className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 text-sm">
                  <div className="flex-shrink-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                    2
                  </div>
                  <div className="text-white pt-0.5">
                    Scroll down and find "Add to Home Screen"
                  </div>
                </div>

                <div className="flex items-start gap-3 text-sm">
                  <div className="flex-shrink-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                    3
                  </div>
                  <div className="flex items-center gap-2 text-white pt-0.5">
                    <span>Tap</span>
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Plus className="w-3 h-3 text-amber-400" />
                      <span className="text-xs font-medium">Add</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 mb-4 border border-white/10">
                <div className="flex items-start gap-2 text-xs text-emerald-100">
                  <div className="text-amber-400 mt-0.5">✓</div>
                  <span>Fast access • Offline support • Full screen experience</span>
                </div>
              </div>

              {/* Don't show again link */}
              <button
                onClick={() => dismissPrompt(true)}
                className="w-full text-center text-sm text-emerald-300 hover:text-emerald-200 transition-colors underline"
              >
                Don't show this again
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default InstallPrompt;
